// apps/share-web/src/chart/reader-prefs.ts
//
// Настройки ЧИТАТЕЛЯ — то, что принадлежит человеку и его телефону, а не
// песне: размер текста, колонки, тема, режимы чтения, что показывать. Один ключ
// localStorage, один JSON, одна точка правды для страницы слов и плеера.
//
// Почему не по песне и не по ссылке: «мне мелко» — свойство глаз и экрана, оно
// не меняется от того, какую песню открыли (та же причина, по которой у плеера
// уже жил в localStorage один шаг кегля — и только он). Тональность здесь НЕ
// хранится: она свойство песни, и остаётся у поверхности до смены песни.
//
// ⚠️ localStorage в приватном режиме БРОСАЕТ, а не возвращает null (тот же
// приём у bw.lib.* в library-view.ts): всё чтение и запись — в try/catch, и
// отказ хранилища означает «настройки живут до перезагрузки», не ошибку.
//
// Миграция: до этой волны плеер хранил шаг кегля 1.1^n под `bw.chart.fs`. При
// первом чтении без нового ключа старое значение переводится в проценты
// (legacyStepToPct) — читатель, накрутивший себе крупный текст, не должен
// проснуться с базовым.

import { clampFontPct, legacyStepToPct } from './chart-font.js';

export type ReaderTheme = 'dark' | 'light' | 'auto';

export interface ReaderPrefs {
  fontPct: number;
  columns: number;
  theme: ReaderTheme;
  fit: boolean;
  follow: boolean;
  showChords: boolean;
  showLyrics: boolean;
}

export const READER_PREFS_KEY = 'bw.reader.v1';
export const LEGACY_FONT_STEP_KEY = 'bw.chart.fs';
/** Три колонки — потолок вёрстки; с волны v2 он один на все экраны (решение Сергея 14.09). */
export const READER_COLUMNS_MAX = 3;

export const DEFAULT_READER_PREFS: ReaderPrefs = {
  fontPct: 0, columns: 1, theme: 'dark', fit: false, follow: false, showChords: true, showLyrics: true,
};

/** Хранилище как его видит модуль. В проде — localStorage; в тестах — объект. */
export interface PrefsStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const THEMES: readonly ReaderTheme[] = ['dark', 'light', 'auto'];

const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);

/** Что угодно → валидные настройки. Каждое поле прижимается отдельно; мусор → дефолт поля. */
export function clampReaderPrefs(raw: unknown): ReaderPrefs {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const cols = Math.round(Number(r.columns));
  return {
    fontPct: clampFontPct(r.fontPct),
    columns: Number.isFinite(cols) ? Math.max(1, Math.min(READER_COLUMNS_MAX, cols)) : DEFAULT_READER_PREFS.columns,
    theme: THEMES.includes(r.theme as ReaderTheme) ? (r.theme as ReaderTheme) : DEFAULT_READER_PREFS.theme,
    fit: bool(r.fit, DEFAULT_READER_PREFS.fit),
    follow: bool(r.follow, DEFAULT_READER_PREFS.follow),
    showChords: bool(r.showChords, DEFAULT_READER_PREFS.showChords),
    showLyrics: bool(r.showLyrics, DEFAULT_READER_PREFS.showLyrics),
  };
}

function defaultStore(): PrefsStore | null {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
}

/** Настройки из хранилища (с миграцией старого шага кегля), или дефолты. Никогда не бросает. */
export function loadReaderPrefs(store: PrefsStore | null = defaultStore()): ReaderPrefs {
  if (!store) return { ...DEFAULT_READER_PREFS };
  try {
    const raw = store.getItem(READER_PREFS_KEY);
    if (raw != null) return clampReaderPrefs(JSON.parse(raw));
    const legacy = store.getItem(LEGACY_FONT_STEP_KEY);
    if (legacy != null) return { ...DEFAULT_READER_PREFS, fontPct: legacyStepToPct(legacy) };
  } catch { /* битый JSON или бросающее хранилище — дефолты */ }
  return { ...DEFAULT_READER_PREFS };
}

/** Слить правку с сохранённым, прижать, записать. Возвращает итог. Никогда не бросает. */
export function saveReaderPrefs(patch: Partial<ReaderPrefs>, store: PrefsStore | null = defaultStore()): ReaderPrefs {
  const next = clampReaderPrefs({ ...loadReaderPrefs(store), ...patch });
  if (store) {
    try { store.setItem(READER_PREFS_KEY, JSON.stringify(next)); } catch { /* приватный режим */ }
  }
  return next;
}
